import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

/**
 * Maps PrimeNG's design tokens onto the Invento design system's own CSS custom properties
 * (see styles.scss) instead of Aura's default palette, so every PrimeNG component re-skins
 * itself automatically whenever those variables change — including the light/dark swap under
 * `prefers-color-scheme`, which PrimeNG's own "system" dark mode also keys off of.
 */
const colorScheme = {
  primary: {
    color: 'var(--accent)',
    contrastColor: '#ffffff',
    hoverColor: 'var(--accent-ink)',
    activeColor: 'var(--accent-ink)',
  },
  highlight: {
    background: 'var(--accent-soft)',
    focusBackground: 'var(--accent-soft)',
    color: 'var(--accent-ink)',
    focusColor: 'var(--accent-ink)',
  },
  surface: {
    0: 'var(--panel)',
    50: 'var(--paper)',
    100: 'var(--line)',
    200: 'var(--line)',
    300: 'var(--line-strong)',
    400: 'var(--line-strong)',
    500: 'var(--ink-soft)',
    600: 'var(--ink-soft)',
    700: 'var(--ink)',
    800: 'var(--ink)',
    900: 'var(--ink)',
    950: 'var(--ink)',
  },
  formField: {
    background: 'var(--panel)',
    disabledBackground: 'var(--paper)',
    filledBackground: 'var(--paper)',
    filledHoverBackground: 'var(--paper)',
    filledFocusBackground: 'var(--paper)',
    borderColor: 'var(--line)',
    hoverBorderColor: 'var(--line-strong)',
    focusBorderColor: 'var(--accent)',
    invalidBorderColor: 'var(--critical)',
    color: 'var(--ink)',
    disabledColor: 'var(--ink-soft)',
    placeholderColor: 'var(--placeholder)',
    invalidPlaceholderColor: 'var(--critical)',
    floatLabelColor: 'var(--ink-soft)',
    floatLabelFocusColor: 'var(--accent)',
    floatLabelActiveColor: 'var(--ink-soft)',
    iconColor: 'var(--ink-soft)',
    shadow: 'var(--shadow-xs)',
  },
  text: {
    color: 'var(--ink)',
    hoverColor: 'var(--ink)',
    mutedColor: 'var(--ink-soft)',
    hoverMutedColor: 'var(--ink-soft)',
  },
  content: {
    background: 'var(--panel)',
    hoverBackground: 'var(--paper)',
    borderColor: 'var(--line)',
    color: '{text.color}',
    hoverColor: '{text.hover.color}',
  },
  overlay: {
    select: {
      background: 'var(--panel)',
      borderColor: 'var(--line)',
      color: '{text.color}',
    },
    popover: {
      background: 'var(--panel)',
      borderColor: 'var(--line)',
      color: '{text.color}',
    },
    modal: {
      background: 'var(--panel)',
      borderColor: 'var(--line)',
      color: '{text.color}',
    },
  },
  list: {
    option: {
      focusBackground: 'var(--paper)',
      selectedBackground: 'var(--accent-soft)',
      selectedFocusBackground: 'var(--accent-soft)',
      color: '{text.color}',
      focusColor: '{text.hover.color}',
      selectedColor: 'var(--accent-ink)',
      selectedFocusColor: 'var(--accent-ink)',
      icon: { color: 'var(--ink-soft)', focusColor: 'var(--ink-soft)' },
    },
    optionGroup: { background: 'transparent', color: '{text.muted.color}' },
  },
  navigation: {
    item: {
      focusBackground: 'var(--paper)',
      activeBackground: 'var(--paper)',
      color: '{text.color}',
      focusColor: '{text.hover.color}',
      activeColor: '{text.hover.color}',
      icon: {
        color: 'var(--ink-soft)',
        focusColor: 'var(--ink-soft)',
        activeColor: 'var(--ink-soft)',
      },
    },
    submenuLabel: { background: 'transparent', color: '{text.muted.color}' },
    submenuIcon: {
      color: 'var(--ink-soft)',
      focusColor: 'var(--ink-soft)',
      activeColor: 'var(--ink-soft)',
    },
  },
};

// The datepicker's calendar-icon button and "today" cell take Aura's {surface.*} scale, which this preset maps onto --ink/--line:
// fine in light, but in dark {surface.800}/{surface.700} resolve to --ink (near white), which painted the icon button as a white
// square and today as a white blob. Pin them to the app's own field/border tokens, the same in both schemes.
const datepickerScheme = {
  dropdown: {
    background: 'var(--paper)',
    hoverBackground: 'var(--line)',
    activeBackground: 'var(--line-strong)',
    color: 'var(--ink-soft)',
    hoverColor: 'var(--ink)',
    activeColor: 'var(--ink)',
  },
  today: { background: 'var(--line)', color: 'var(--ink)' },
};

const selectGeometry = {
  root: { paddingX: 'var(--select-pad-x)', paddingY: '0' },
  dropdown: { width: 'var(--select-chevron-slot)' },
};

export const InventoPreset = definePreset(Aura, {
  primitive: {
    // Aura's Button theme interpolates severity="danger" straight from this red scale (e.g.
    // `background: {red.500}`), so routing it through the app's own --critical token here is the
    // one place that makes every `p-button-danger` (ConfirmDialog's accept button included) match
    // the same red already used for delete/danger actions elsewhere (see --critical in styles.scss).
    red: {
      50: 'var(--critical-soft)',
      100: 'var(--critical-soft)',
      200: 'var(--critical)',
      300: 'var(--critical)',
      400: 'var(--critical)',
      500: 'var(--critical)',
      600: 'color-mix(in srgb, var(--critical) 85%, black)',
      700: 'color-mix(in srgb, var(--critical) 70%, black)',
      800: 'color-mix(in srgb, var(--critical) 70%, black)',
      900: 'color-mix(in srgb, var(--critical) 70%, black)',
      950: 'color-mix(in srgb, var(--critical) 60%, black)',
    },
  },
  semantic: {
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'var(--shadow-focus)',
    },
    formField: {
      borderRadius: 'var(--radius-control)',
    },
    content: {
      borderRadius: 'var(--radius-card)',
    },
    overlay: {
      select: { borderRadius: 'var(--radius-control)' },
      popover: { borderRadius: 'var(--radius-card)' },
      modal: { borderRadius: 'var(--radius-card)' },
    },
    // Both branches are identical on purpose — they resolve to the same CSS variables, which
    // already carry separate light/dark values (see styles.scss). PrimeNG's own "system"
    // dark-mode switch (also prefers-color-scheme-based) just picks between two equal token
    // sets, so there is exactly one place that owns light/dark colors: styles.scss.
    colorScheme: {
      light: colorScheme,
      dark: colorScheme,
    },
  },
  components: {
    // The select family's geometry, from the --select-* custom properties in styles.scss ("SELECT CONVENTION"): one horizontal
    // padding, no vertical padding (the label's line-height gives the height), one chevron slot.
    select: selectGeometry,
    multiselect: selectGeometry,
    datepicker: { colorScheme: { light: datepickerScheme, dark: datepickerScheme } },
  },
});
